import { FunctionalComponent } from 'preact'
import { useState } from 'preact/hooks'
import { X, Save, Undo2, Loader2 } from 'lucide-preact'
import type { LeadSignalTimelineDto } from '@/api/leads'
import { leadsApi, type LeadUpdateDto } from '@/api/leads'
import { handleApiError, setFormValidationErrors } from '@/utils/errorHandling'
import { useTheme } from '@/hooks/useTheme'
import { useToast } from '@/hooks/useToast'
import { useForm } from 'react-hook-form'
import { isValidEmail } from '@/utils/validation'
import { PersonAvatar } from './Avatar'
import { CompanyEditSection } from './CompanyEditSection'

export interface ContactSidePanelProps {
  contact: LeadSignalTimelineDto
  workspaceId: string
  campaignId: string
  onClose: () => void
  onContactUpdated?: (updated: Partial<LeadSignalTimelineDto> & { Id: string }) => void
}

export const ContactSidePanel: FunctionalComponent<ContactSidePanelProps> = ({ contact, workspaceId, campaignId, onClose, onContactUpdated }) => {
  const { resolvedTheme } = useTheme()
  const { showError } = useToast()
  const isDark = resolvedTheme === 'dark'
  const fullName = [contact.FirstName, contact.LastName].filter(Boolean).join(' ') || contact.Email || 'Contact'

  const [saving, setSaving] = useState(false)
  const [enrichLoading, setEnrichLoading] = useState(false)

  // Direct access (DTO already includes optional Email)
  const contactEmail = contact.Email || ''
  // A lead keyed on its LinkedIn member id may stay without an email; one that has an email must keep one.
  const emailIsOptional = contactEmail === ''
  const [form, setForm] = useState<LeadUpdateDto & { CompanyWebsite?: string | null }>(() => ({
    ETag: contact.ETag,
    Email: contactEmail,
    FirstName: contact.FirstName || '',
    LastName: contact.LastName || '',
    JobTitle: contact.JobTitle || '',
    Phone: contact.Phone || '',
    CompanyLinkedInId: contact.CompanyLinkedInId || '',
    CompanyName: contact.Company || '',
    Industry: contact.Industry || '',
    CompanySize: contact.CompanySize || '',
    CompanyWebsite: contact.CompanyWebsite || ''
  }))
  // react-hook-form for declarative validation rules mirroring server logic
  interface ContactFormFields {
    Email: string
    FirstName: string
    LastName: string
    JobTitle: string
    Phone: string
    CompanyLinkedInId: string
    CompanyName?: string
    Industry?: string
    CompanySize?: string
    CompanyWebsite?: string
  }
  const { register, handleSubmit, formState: { errors }, setError, setValue, trigger } = useForm<ContactFormFields>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      Email: contactEmail,
      FirstName: contact.FirstName || '',
      LastName: contact.LastName || '',
      JobTitle: contact.JobTitle || '',
      Phone: contact.Phone || '',
      CompanyLinkedInId: contact.CompanyLinkedInId || '',
      CompanyName: contact.Company || '',
      Industry: contact.Industry || '',
      CompanySize: contact.CompanySize || '',
      CompanyWebsite: contact.CompanyWebsite || ''
    }
  })

  function updateField<K extends keyof LeadUpdateDto>(key: K, value: LeadUpdateDto[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  // Determine if any relevant persisted fields have changed vs original contact
  const isChanged =
    (form.Email || '').trim() !== (contactEmail || '').trim() ||
    (form.FirstName || '').trim() !== (contact.FirstName || '').trim() ||
    (form.LastName || '').trim() !== (contact.LastName || '').trim() ||
    (form.JobTitle || '').trim() !== (contact.JobTitle || '').trim() ||
    (form.Phone || '').trim() !== (contact.Phone || '').trim() ||
    (form.CompanyLinkedInId || '').trim() !== (contact.CompanyLinkedInId || '').trim() ||
    (form.CompanyName || '').trim() !== (contact.Company || '').trim() ||
    (form.Industry || '').trim() !== (contact.Industry || '').trim() ||
    (form.CompanySize || '').trim() !== (contact.CompanySize || '').trim() ||
    (form.CompanyWebsite || '').trim() !== (contact.CompanyWebsite || '').trim()


  const performSave = async (validData?: ContactFormFields) => {
    setSaving(true)
    try {
      // Merge validated data into local form state (react-hook-form controls)
      if (validData) {
        setForm(f => ({ ...f, ...validData }))
      }
      const working = validData ? { ...form, ...validData } : { ...form }
      const payload: LeadUpdateDto = {
        ETag: form.ETag,
        // Trimmed: the optional field takes spaces alone as no email, and the API would refuse them.
        Email: working.Email?.trim() || null,
        FirstName: working.FirstName || null,
        LastName: working.LastName || null,
        JobTitle: working.JobTitle || null,
        Phone: working.Phone || null,
        CompanyLinkedInId: working.CompanyLinkedInId || null,
        CompanyName: working.CompanyName || null,
        Industry: working.Industry || null,
        CompanySize: working.CompanySize || null,
        CompanyWebsite: working.CompanyWebsite || null
      }
      const resp = await leadsApi.update(workspaceId, campaignId, contact.LeadId, payload)
      if (resp.error) {
        // Global handler shows toasts for 422 validation errors
        if (resp.status === 422) {
          // Set form field errors
          setFormValidationErrors(resp, setError)
        } else {
          // For non-422 errors, use handleApiError
          handleApiError(resp, showError, () => { }, 'Save Failed')
        }
        setSaving(false)
        return
      }
      onContactUpdated?.({
        Id: contact.LeadId,
        ETag: resp.data?.ETag || contact.ETag, // Use updated ETag from response
        Email: payload.Email,
        FirstName: payload.FirstName,
        LastName: payload.LastName,
        JobTitle: payload.JobTitle,
        Phone: payload.Phone,
        CompanyLinkedInId: payload.CompanyLinkedInId,
        Company: payload.CompanyName,
        Industry: payload.Industry,
        CompanySize: payload.CompanySize,
        CompanyWebsite: form.CompanyWebsite
      })
      onClose()
    } catch (e) {
      // Non-422 errors need manual handling
      handleApiError(e, showError, () => { }, 'Save Failed')
    } finally { setSaving(false) }
  }

  function handleCompanyChange(linkedInId: string, companyName: string, industry?: string, companySize?: string, companyWebsite?: string) {
    setForm(f => ({
      ...f,
      CompanyLinkedInId: linkedInId,
      CompanyName: companyName,
      Industry: industry || '',
      CompanySize: companySize || '',
      CompanyWebsite: companyWebsite || ''
    }))
    // Update react-hook-form's field values
    setValue('CompanyLinkedInId', linkedInId, { shouldValidate: true, shouldDirty: true })
    setValue('CompanyName', companyName, { shouldValidate: true, shouldDirty: true })
    setValue('Industry', industry || '', { shouldValidate: true, shouldDirty: true })
    setValue('CompanySize', companySize || '', { shouldValidate: true, shouldDirty: true })
    setValue('CompanyWebsite', companyWebsite || '', { shouldValidate: true, shouldDirty: true })
  }

  return (
    <div class="fixed inset-0 z-50 flex" aria-modal="true" role="dialog">
      {/* Backdrop */}
      <div class="flex-1 bg-slate-900/40" onClick={onClose} />
      {/* Panel */}
      <div class={`w-full sm:w-[460px] lg:w-[520px] xl:w-[560px] ml-auto h-full border-l shadow-xl flex flex-col ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`} data-testid="contact-side-panel">
        <div class="p-6 flex-1 overflow-y-auto">
          <div class="flex items-start justify-between mb-4">
            <div class="flex flex-col gap-1">
              <h2 class={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                Contact Details
              </h2>
            </div>
            <div class="flex items-center gap-2">
              <button disabled={saving || enrichLoading || !isChanged} onClick={handleSubmit(data => performSave(data))} class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-primary-600 hover:bg-primary-700 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50" title={!isChanged ? 'No changes to save' : 'Save changes'}>
                {saving ? <Loader2 class="w-3.5 h-3.5 animate-spin" /> : <Save class="w-3.5 h-3.5" />} Save
              </button>
              <button disabled={saving || enrichLoading} onClick={onClose} class={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}>
                <Undo2 class="w-3.5 h-3.5" /> Cancel
              </button>
              <button onClick={onClose} class="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500">
                <X class="w-4 h-4" />
                <span class="sr-only">Close panel</span>
              </button>
            </div>
          </div>
          {/* Header avatar & name */}
          <div class="flex items-center gap-4 mb-6">
            <div class="ring-4 ring-white dark:ring-slate-900 rounded-full">
              <PersonAvatar
                firstName={contact.FirstName}
                lastName={contact.LastName}
                fullName={fullName}
                profilePicUrl={contact.ProfilePicUrl}
                size="xl"
              />
            </div>
            <div class="min-w-0 flex-1">
              <div class={`text-xl font-semibold truncate ${isDark ? 'text-slate-100' : 'text-slate-900'}`} title={fullName}>{fullName}</div>
              <div class="mt-2">
                <label class={`text-[11px] uppercase tracking-wide font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Job Title</label>
                <input
                  value={form.JobTitle || ''}
                  {...register('JobTitle', {
                    maxLength: { value: 200, message: 'Job Title must be 200 characters or fewer.' },
                    onChange: e => updateField('JobTitle', (e.target as HTMLInputElement).value)
                  })}
                  placeholder="e.g. VP of Sales"
                  class={`mt-1 w-full px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${errors.JobTitle ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'} ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'}`}
                />
                {errors.JobTitle && <span class={`text-[11px] mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{errors.JobTitle.message}</span>}
              </div>
            </div>
          </div>
          {/* Key fields grid */}
          <div class="grid grid-cols-1 gap-y-4 text-sm mb-8">
            {/* Email */}
            <div class="flex flex-col gap-1">
              <div class="flex items-baseline gap-2">
                <label class={`text-[11px] uppercase tracking-wide font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Email</label>
                {emailIsOptional && <span class={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Optional</span>}
              </div>
              <input
                type="email"
                value={form.Email || ''}
                {...register('Email', {
                  required: !emailIsOptional && 'Email cannot be blank.',
                  maxLength: { value: 254, message: 'Email address is too long (max 254 characters).' },
                  validate: (value) => (emailIsOptional && value.trim() === '') || isValidEmail(value) || 'Email format is invalid.',
                  onChange: e => updateField('Email', (e.target as HTMLInputElement).value)
                })}
                placeholder={emailIsOptional ? 'Add an email address' : 'Email address'}
                class={`px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${errors.Email ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'} ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'}`}
              />
              {/* The API's refusal of an email another lead already has lands here too, naming Merge. */}
              {errors.Email ? (
                <span class={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>{errors.Email.message}</span>
              ) : emailIsOptional && (
                <span class={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>This lead came from LinkedIn without one.</span>
              )}
            </div>

            {/* First / Last Name */}
            <div class="flex flex-col gap-2">
              <div class="flex gap-2">
                <div class="flex-1 flex flex-col gap-1">
                  <label class={`text-[11px] uppercase tracking-wide font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>First Name</label>
                  <input
                    value={form.FirstName || ''}
                    {...register('FirstName', {
                      required: 'FirstName cannot be blank.',
                      maxLength: { value: 100, message: 'FirstName must be 100 characters or fewer.' },
                      validate: v => v.trim().length > 0 || 'FirstName cannot be blank.',
                      onChange: e => updateField('FirstName', (e.target as HTMLInputElement).value)
                    })}
                    placeholder="First Name"
                    class={`w-full px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${errors.FirstName ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'} ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'}`}
                  />
                  {errors.FirstName && <span class={`text-[11px] mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{errors.FirstName.message}</span>}
                </div>
                <div class="flex-1 flex flex-col gap-1">
                  <label class={`text-[11px] uppercase tracking-wide font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Last Name</label>
                  <input
                    value={form.LastName || ''}
                    {...register('LastName', {
                      required: 'LastName cannot be blank.',
                      maxLength: { value: 100, message: 'LastName must be 100 characters or fewer.' },
                      validate: v => v.trim().length > 0 || 'LastName cannot be blank.',
                      onChange: e => updateField('LastName', (e.target as HTMLInputElement).value)
                    })}
                    placeholder="Last Name"
                    class={`w-full px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${errors.LastName ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'} ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'}`}
                  />
                  {errors.LastName && <span class={`text-[11px] mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{errors.LastName.message}</span>}
                </div>
              </div>
            </div>

            {/* Phone */}
            <div class="flex flex-col gap-1">
              <label class={`text-[11px] uppercase tracking-wide font-semibold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Phone</label>
              <input
                type="tel"
                value={form.Phone || ''}
                {...register('Phone', {
                  maxLength: { value: 50, message: 'Phone must be 50 characters or fewer.' },
                  onChange: e => updateField('Phone', (e.target as HTMLInputElement).value)
                })}
                placeholder="e.g. +1 (555) 123-4567"
                class={`px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 ${errors.Phone ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500'} ${isDark ? 'border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-500' : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400'}`}
              />
              {errors.Phone && <span class={`text-[11px] mt-1 ${isDark ? 'text-red-400' : 'text-red-600'}`}>{errors.Phone.message}</span>}
            </div>

            {/* Company controls */}
            <CompanyEditSection
              companyLinkedInId={form.CompanyLinkedInId || ''}
              companyName={form.CompanyName}
              industry={form.Industry}
              companySize={form.CompanySize}
              companyWebsite={form.CompanyWebsite}
              originalCompanyLinkedInId={contact.CompanyLinkedInId || undefined}
              isDark={isDark}
              saving={saving}
              errors={errors}
              register={register}
              trigger={trigger}
              onChange={handleCompanyChange}
              onLoadingChange={setEnrichLoading}
            />
          </div>
        </div>
      </div>
    </div>
  )
}